import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Context } from '../controls/advanced-gridview/models/context';
import { FilterInfos } from '../controls/advanced-gridview/models/filter-infos';
import { Results } from '../controls/advanced-gridview/models/results-T';
import { SortInfos } from '../controls/advanced-gridview/models/sort-infos';
import { AdvancedService } from '../controls/advanced-gridview/services/advanced-service';
import { IListLayoutAccessibility } from '../models/interfaces/list-layout-accessibility.interface';
import { ListLayout } from '../models/list-layout';
import { ListLayoutAccessibility } from '../models/list-layout-accessibility';
import { CrudService } from './crud-service';
import { MemoryService } from './memory.service';

@Injectable({
    providedIn: 'root',
})
export class ListLayoutService extends CrudService implements AdvancedService<ListLayout> {
    constructor(
        private http: HttpClient,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    /**
     * Liste les listLayout
     */
    List(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos, pageNumber = 0, pageSize = 100): Observable<Results<ListLayout>> {
        let params = new Map<string, string | number | boolean>();
        params.set('from', pageNumber);
        params.set('size', pageSize);

        const httpOptions = this.getListHttpOptions(context, filters, generalFilters, sortInfos, params);

        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listLayouts`, httpOptions).pipe(
            map((x) => {
                var res = Results.getCopyFrom<ListLayout>(ListLayout, x);

                return res;
            })
        );
    }

    /**
     * Exporte les listLayout
     */
    Export(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos): Observable<Blob> {
        const headers = this.getExportHeader(context, filters, generalFilters, sortInfos);

        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listLayouts/export`, { headers: headers, responseType: 'blob' }).pipe(
            map((res: any) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }

    /**
     * Mise à jour d'un ListLayout
     */
    update(listLayoutId: number, listLayout: ListLayout): Observable<ListLayout> {
        return this.http
            .put<ListLayout>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listLayouts/${listLayoutId}`, listLayout, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /**
     * Suppression d'un listLayout
     */
    delete(listLayoutId: number): Observable<boolean> {
        return this.http
            .delete<ListLayout>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listLayouts/${listLayoutId}`, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return true;
                })
            );
    }

    /**
     * Création d'un listLayout
     */
    add(listLayout: ListLayout): Observable<ListLayout> {
        listLayout.organizationId = this.organizationId;
        return this.http
            .post<ListLayout>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listLayouts`, listLayout, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /**
     * Récupération d'un listLayout
     */
    get(listLayoutId: number): Observable<ListLayout> {
        return this.http
            .get<ListLayout>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listLayouts/${listLayoutId}`, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /** Retourne la liste des accessibilité au listLayout */
    getAccessibilities(listLayoutId: number): Observable<ListLayoutAccessibility[]> {
        return this.http
            .get<ListLayoutAccessibility[]>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listLayouts/${listLayoutId}/accessibilities`,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /**Retourne la liste des accessibilité interdit pour un listLayaout */
    getAccessibilitiesForbidden(listLayoutId: number): Observable<ListLayoutAccessibility[]> {
        return this.http
            .get<ListLayoutAccessibility[]>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listLayouts/${listLayoutId}/accessibilities/forbidden`,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /** Mise à jour des accessibilité */
    updateAccessibilities(listLayoutId: number, accessibilities: IListLayoutAccessibility[]): Observable<IListLayoutAccessibility[]> {
        return this.http
            .post<IListLayoutAccessibility[]>(
                this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listLayouts/${listLayoutId}/accessibilities`,
                accessibilities,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    /** Export des accessibilité */
    exportAccessibilities(listLayoutId: number): Observable<Blob> {
        return this.http
            .get(this.baseUrl + `/api/organizations/${this.organizationId}/views/${this.viewId}/listLayouts/${listLayoutId}/accessibilities/export`, {
                responseType: 'blob',
            })
            .pipe(
                map((res: any) => {
                    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    return blob;
                })
            );
    }
}
